(() => {
  const ELEMENT_NAME = 'ttc-product-event-information';
  const DANGEROUS_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'IFRAME',
    'OBJECT',
    'EMBED',
    'SVG',
    'MATH',
    'FORM',
    'INPUT',
    'BUTTON',
    'LINK',
    'META',
  ]);
  const ALLOWED_TAGS = new Set([
    'P',
    'BR',
    'STRONG',
    'B',
    'EM',
    'I',
    'UL',
    'OL',
    'LI',
    'A',
    'H3',
    'H4',
    'H5',
    'BLOCKQUOTE',
  ]);

  class TTCProductEventInformation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.sectionId = this.dataset.sectionId;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en-AU';
      this.timeZone = this.dataset.timeZone || undefined;
      this.productTitle = this.dataset.productTitle || '';
      this.showEventName = this.dataset.showEventName !== 'false';
      this.showDateTime = this.dataset.showDateTime !== 'false';
      this.showFullDescription = this.dataset.showFullDescription !== 'false';
      this.readBootstrap(this);
      this.render(this.dataset.selectedVariantId, false);

      this.variantPicker = document.querySelector(
        `variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`
      );
      if (this.variantPicker) {
        this.variantPicker.addEventListener('change', this.handlePickerChange);
      }

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.variantPicker) {
        this.variantPicker.removeEventListener('change', this.handlePickerChange);
      }
      window.clearTimeout(this.loadingTimeout);
    }

    handlePickerChange = () => {
      this.setLoading(true);
      window.clearTimeout(this.loadingTimeout);
      this.loadingTimeout = window.setTimeout(() => this.setLoading(false), 10000);
    };

    readBootstrap(source) {
      this.events = this.parseJson(source.querySelector('[data-ttc-event-data]'), []);
      this.variantMap = this.parseJson(source.querySelector('[data-ttc-variant-event-map]'), []);
    }

    parseJson(script, fallback) {
      if (!script) return fallback;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('TTC event information could not read its Abbl data.', error);
        return fallback;
      }
    }

    handleVariantChange({ data } = {}) {
      if (!data?.variant || data.sectionId !== this.sectionId) return;

      if (data.html) {
        const source = Array.from(data.html.querySelectorAll(ELEMENT_NAME)).find(
          (component) => component.dataset.sectionId === this.sectionId
        );
        if (source) this.readBootstrap(source);
      }

      this.render(data.variant.id, true);
    }

    resolveEvent(variantId) {
      const mapping = this.variantMap.find(
        (entry) => String(entry.variantId) === String(variantId)
      );
      if (!mapping) return null;

      if (mapping.eventId !== undefined && mapping.eventId !== null && String(mapping.eventId).trim()) {
        const event = this.events.find(
          (entry) => String(entry.id) === String(mapping.eventId)
        );
        return event ? { event, mapping } : null;
      }

      if (!mapping.eventOptionValue) return null;
      const event = this.events.find(
        (entry) => String(entry.optionValue) === String(mapping.eventOptionValue)
      );
      return event ? { event, mapping } : null;
    }

    render(variantId, announce) {
      window.clearTimeout(this.loadingTimeout);
      const resolved = this.resolveEvent(variantId);
      const announcement = this.querySelector('[data-ttc-event-announcement]');

      if (!resolved) {
        this.setLoading(false);
        this.hidden = true;
        if (announcement) announcement.textContent = '';
        return;
      }

      const { event, mapping } = resolved;
      this.hidden = false;
      this.setLoading(false);

      const name = String(event.name || '').trim();
      const nameElement = this.querySelector('[data-ttc-event-name]');
      const nameAddsInformation =
        this.showEventName &&
        name &&
        name.toLocaleLowerCase(this.locale) !== this.productTitle.trim().toLocaleLowerCase(this.locale);
      this.setText(nameElement, nameAddsInformation ? name : '');

      const dateElement = this.querySelector('[data-ttc-event-date]');
      const dateText = this.showDateTime
        ? this.renderDateRange(dateElement, event.start, event.finish)
        : '';
      if (!this.showDateTime && dateElement) {
        dateElement.replaceChildren();
        dateElement.hidden = true;
      }

      this.setText(
        this.querySelector('[data-ttc-event-lead]'),
        this.toPlainText(event.shortDescription)
      );

      const description = this.querySelector('[data-ttc-event-description]');
      if (description) {
        description.replaceChildren();
        if (this.showFullDescription && event.description) {
          const safeContent = this.sanitizeRichText(String(event.description));
          description.append(...safeContent.childNodes);
        }
        description.hidden = !description.textContent.trim();
      }

      const available = Boolean(mapping.available);
      const availability = this.querySelector('[data-ttc-event-availability]');
      if (availability) {
        availability.textContent = available
          ? 'This session is available.'
          : 'This session is currently sold out or unavailable.';
        availability.classList.toggle('ttc-product-event__availability--available', available);
        availability.classList.toggle('ttc-product-event__availability--unavailable', !available);
      }

      if (announce && announcement) {
        const summary = [
          nameAddsInformation ? name : this.productTitle,
          dateText,
          available ? 'Available.' : 'Sold out or unavailable.',
        ].filter(Boolean);
        announcement.textContent = summary.join(' ');
      }
    }

    setText(element, text) {
      if (!element) return;
      element.textContent = text;
      element.hidden = !text;
    }

    setLoading(isLoading) {
      const loading = this.querySelector('[data-ttc-event-loading]');
      const content = this.querySelector('[data-ttc-event-content]');
      if (!loading || !content) return;

      if (isLoading) this.hidden = false;
      loading.hidden = !isLoading;
      content.hidden = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
    }

    toPlainText(value) {
      if (!value) return '';
      const documentFragment = new DOMParser().parseFromString(String(value), 'text/html');
      return documentFragment.body.textContent.trim();
    }

    renderDateRange(container, startValue, finishValue) {
      if (!container || !startValue) {
        if (container) container.hidden = true;
        return '';
      }

      const start = new Date(startValue);
      const finish = finishValue ? new Date(finishValue) : null;
      if (Number.isNaN(start.getTime())) {
        container.hidden = true;
        return '';
      }

      const dateOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      };
      const timeOptions = { hour: 'numeric', minute: '2-digit' };
      const dayOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
      if (this.timeZone) {
        dateOptions.timeZone = this.timeZone;
        timeOptions.timeZone = this.timeZone;
        dayOptions.timeZone = this.timeZone;
      }

      let fullFormatter;
      let timeFormatter;
      let dayFormatter;
      try {
        fullFormatter = new Intl.DateTimeFormat(this.locale, dateOptions);
        timeFormatter = new Intl.DateTimeFormat(this.locale, timeOptions);
        dayFormatter = new Intl.DateTimeFormat(this.locale, dayOptions);
      } catch (error) {
        delete dateOptions.timeZone;
        delete timeOptions.timeZone;
        delete dayOptions.timeZone;
        fullFormatter = new Intl.DateTimeFormat(this.locale, dateOptions);
        timeFormatter = new Intl.DateTimeFormat(this.locale, timeOptions);
        dayFormatter = new Intl.DateTimeFormat(this.locale, dayOptions);
      }

      container.replaceChildren();
      const startTime = document.createElement('time');
      startTime.dateTime = String(startValue);
      startTime.textContent = fullFormatter.format(start);
      container.append(startTime);

      let dateText = startTime.textContent;
      if (
        finish &&
        !Number.isNaN(finish.getTime()) &&
        finish.getTime() !== start.getTime()
      ) {
        const separator = document.createTextNode('–');
        const finishTime = document.createElement('time');
        finishTime.dateTime = String(finishValue);
        const sameDay = dayFormatter.format(start) === dayFormatter.format(finish);
        finishTime.textContent = sameDay
          ? timeFormatter.format(finish)
          : fullFormatter.format(finish);
        container.append(separator, finishTime);
        dateText += `–${finishTime.textContent}`;
      }

      container.hidden = false;
      return dateText;
    }

    sanitizeRichText(value) {
      const parsed = new DOMParser().parseFromString(value, 'text/html');
      const elements = Array.from(parsed.body.querySelectorAll('*'));

      elements.forEach((element) => {
        if (DANGEROUS_TAGS.has(element.tagName)) {
          element.remove();
          return;
        }

        if (!ALLOWED_TAGS.has(element.tagName)) {
          element.replaceWith(...element.childNodes);
          return;
        }

        const href = element.tagName === 'A' ? element.getAttribute('href') : null;
        const title = element.tagName === 'A' ? element.getAttribute('title') : null;
        Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));

        if (element.tagName === 'A' && this.isSafeUrl(href)) {
          element.setAttribute('href', href.trim());
          if (title) element.setAttribute('title', title);
        } else if (element.tagName === 'A') {
          element.replaceWith(...element.childNodes);
        }
      });

      if (!parsed.body.children.length && parsed.body.textContent.trim()) {
        const lines = parsed.body.textContent.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
        parsed.body.replaceChildren();
        lines.forEach((line) => {
          const paragraph = parsed.createElement('p');
          paragraph.textContent = line;
          parsed.body.append(paragraph);
        });
      }

      return parsed.body;
    }

    isSafeUrl(value) {
      if (!value) return false;
      return /^(https?:|mailto:|tel:|\/|#|\?)/i.test(value.trim());
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, TTCProductEventInformation);
  }
})();
